import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { IconTab } from "@/components/confluence/product-tour/src/icon-tab";

import { DatabasesIcon } from "../icons/databases-icon";
import { PagesIcon } from "../icons/pages-icon";
import { SpacesIcon } from "../icons/spaces-icon";
import { WhiteboardsIcon } from "../icons/whiteboards-icon";

import { DatabasesContentType } from "./content-types/databases-content-type";
import { PagesContentType } from "./content-types/pages-content-type";
import { SpacesContentType } from "./content-types/spaces-content-type";
import { WhiteboardsContentType } from "./content-types/whiteboards-content-type";
import {
    tabbedHeroSectionDatabasesMessages,
    tabbedHeroSectionPagesMessages,
    tabbedHeroSectionSpacesMessages,
    tabbedHeroSectionWhiteboardsMessages
} from "./messages/tabbed-hero-section.messages";

const tabStyles = css({
    "maxWidth": 1114,
    "marginInline": "auto",
    ".icon-tab-list": {
        paddingBlockStart: 8,
        margin: 0,
        width: "100%",
        justifyContent: "space-between",
        [media.above.md]: {
            paddingInline: 24
        },
        [media.below.md]: {
            paddingInline: 16,
            gap: 16
        }
    },
    ".icon-tab": {
        borderRadius: 2
    }
});

const contentContainer = css({
    "display": "flex",
    "flexDirection": "column",
    "marginInline": "auto",
    "paddingBlockStart": 32,
    "minHeight": 460,
    [media.above.md]: {
        paddingInline: 24
    },
    [media.below.md]: {
        justifyContent: "end",
        paddingInline: 16,
        paddingBlockStart: 32
    },
    [media.only.sm]: {
        maxWidth: "75%"
    },

    "@keyframes opacityIn": {
        "0%": {
            opacity: 0
        },
        "100%": {
            opacity: 1
        }
    },

    "animation": `opacityIn linear 500ms 1`
});

const ContentTypesSection = () => {
    const intl = useIntl();
    const tabList: {
        title: string;
        icon: ReactNode;
        content: ReactNode;
        lozenge?: string;
        eventProps?: {
            label?: string;
            labelEnglish?: string;
        };
    }[] = [
        {
            title: intl.formatMessage(tabbedHeroSectionPagesMessages.tabName),
            icon: <PagesIcon width="1.5em" height="auto" />,
            content: <PagesContentType />,
            eventProps: {
                label: "content-type-tab-pages",
                labelEnglish: "Pages"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionWhiteboardsMessages.tabName
            ),
            icon: <WhiteboardsIcon width="1.5em" height="auto" />,
            content: <WhiteboardsContentType />,
            eventProps: {
                label: "content-type-tab-whiteboard",
                labelEnglish: "Whiteboards"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionDatabasesMessages.tabName
            ),
            icon: <DatabasesIcon width="1.5em" height="auto" />,
            lozenge: intl.formatMessage(
                tabbedHeroSectionDatabasesMessages.tabLozenge
            ),
            content: <DatabasesContentType />,
            eventProps: {
                label: "content-type-tab-databases",
                labelEnglish: "Databases"
            }
        },
        {
            title: intl.formatMessage(tabbedHeroSectionSpacesMessages.tabName),
            icon: <SpacesIcon width="1.5em" height="auto" />,
            content: <SpacesContentType />,
            eventProps: {
                label: "content-type-tab-spaces",
                labelEnglish: "Spaces"
            }
        }
    ];

    return (
        <UFOSegment name="confluence-pt-cfind-1234-contentTypesSection">
            <div css={tabStyles}>
                <Adg4Tabs>
                    <Adg4DraggableTabList className="icon-tab-list">
                        {tabList.map((tab, index) => (
                            <Adg4DraggableTab
                                key={index}
                                className="icon-tab"
                                dataEventProps={{
                                    "data-product-key": "confluence",
                                    "data-event": "clicked",
                                    "data-event-component": "tab",
                                    "data-event-container":
                                        "contentTypesSection",
                                    "data-label":
                                        tab.eventProps?.label ??
                                        "content-type-tab",
                                    "data-label-english":
                                        tab.eventProps?.labelEnglish,
                                    "data-track-event":
                                        "confluence-pt.content-types.tab"
                                }}
                            >
                                <IconTab
                                    icon={tab.icon}
                                    title={tab.title}
                                    lozenge={tab.lozenge}
                                />
                            </Adg4DraggableTab>
                        ))}
                    </Adg4DraggableTabList>
                    {tabList.map((tab, index) => (
                        <Adg4TabPanel key={index}>
                            <div css={contentContainer}>{tab.content}</div>
                        </Adg4TabPanel>
                    ))}
                </Adg4Tabs>
            </div>
        </UFOSegment>
    );
};

ContentTypesSection.displayName = "ContentTypesSection";

export default ContentTypesSection;
